import React, {useState, useEffect} from 'react';
import {View, ScrollView, Text, Image, Pressable, Modal} from 'react-native';
import {ObjectId, UUID} from 'bson';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import {TemplateCategoryTypes} from '../../Constants/TemplateCategoryTypes';
import {
  Submission,
  SubmissionStatus,
  TemplateVersion,
  Template,
} from '../../Models/PowerSyncModels/models';
import {SubmissionStatuses} from '../../Constants/SubmissionStatuses';
import {Navigation} from '../../Constants/Navigation';
import {QuickAddListSheet} from '../../Styles/QuickAddListSheet';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';
import Colors from '../../Styles/Shared/Colors';
import {Images} from '../../Constants/Images';
import usePowerSyncWriteData from '../../Hooks/usePowerSyncWriteData';
import {usePowerSyncUser} from '../../Providers/PowerSyncUserProvider';
import GeneralHelperService from '../../Services/GeneralHelperService';
import { IGeneralHelperService } from '../../Services/Interfaces/IGeneralHelperService';

type QuickAddListProps = {
  navigation: any;
};

const QuickAddList = (props: QuickAddListProps): React.ReactElement => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [versions, setVersions] = useState<TemplateVersion[]>([]);
  const [showModal, setShowModal] = useState(false);

  const {
    templates: db_templates,
    templateVersions,
    submissionStatuses: db_submissionStatuses,
  } = usePowerSyncData();

  const {updateSubmission, createSubmission} = usePowerSyncWriteData();

  const user = usePowerSyncUser();
  const helperService: IGeneralHelperService = new GeneralHelperService();
  
  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [db_templates, templateVersions]);

  const fetchTemplates = async () => {
    let filteredTemplates = db_templates
      .filter(
        t =>
          t.templateCategory.templateCategoryName ==
          TemplateCategoryTypes.QUICKADD,
      )
      .sort(x => x.row);

    let filteredTemplatesIds = filteredTemplates.map(t => t._id);

    let filteredVersions: TemplateVersion[] = [];
    let allVersions = templateVersions.filter(v =>
      filteredTemplatesIds.includes(v.templateId),
    );
    filteredTemplates.forEach(t => {
      let templateVersions = allVersions
        .filter(v => v.templateId === t._id)
        .sort((a, b) => b.version - a.version);

      filteredVersions.push(templateVersions[0]);
    });

    setTemplates(filteredTemplates);
    setVersions(filteredVersions);
  };

  async function openQuickAdd(templateId: string): Promise<void> {
    let template = templates.find(t => t._id === templateId);

    if (template) {
      let version = versions.find(v => v.templateId === templateId);

      if (!version) return;

      let newDraftId = new ObjectId().toString();
      const draftStatus: SubmissionStatus = db_submissionStatuses.find(
        x => x.name == SubmissionStatuses.DRAFT,
      )!;

      let currentUser = user.getUserInfo();
      let org = '';
      let currentEmail = '';
      if (currentUser) {
        org = currentUser.orgId.toString();
        currentEmail = currentUser.email;
      }

      let newSubmission: Submission = {
        _id: newDraftId,
        partition: org,
        createdBy: currentEmail as string,
        updatedBy: currentEmail as string,
        templateVersion: {
          _id: version._id,
          name: version.name,
        },
        templateType: {
          _id: template!.templateType._id,
          name: template!.templateType.name,
        },
        submissionStatus: {
          _id: draftStatus._id,
          name: draftStatus.name,
        },
        SQLServerId: new UUID().toString().toUpperCase(),
        answers: [],
        metadataJSON: '',
        createDateTimeStamp: new Date(),
        updateDateTimeStamp: new Date(),
        attachmentsUploaded: true,
      };

      await createSubmission(newSubmission);

      if (newDraftId == null) throw Error('Error: create draft failed!');

      setShowModal(false);

      props.navigation.navigate(Navigation.DYNAMICFORM, {
        submissionId: newDraftId,
      });
    }
  }

  const renderTemplates = (): React.ReactElement => {
    return (
      <React.Fragment>
        {templates.map((template, index) => {
          let version = versions.find(
            v => v && v.templateId && v.templateId === template._id,
          );
          if (!version) return null;
          return (
            <Pressable
              key={index}
              onPress={() => {
                openQuickAdd(template._id);
              }}
              style={({pressed}) => [
                pressed && {
                  opacity: 0.8,
                },
                QuickAddListSheet.quickAddListItem,
              ]}>
              {template.iconSvg !== '' && (
                <img
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(
                    helperService.getTemplateIcon(template.iconSvg, Colors.white, Colors.green)
                  )}`}
                />
              )}
              <Text style={QuickAddListSheet.quickAddListText}>
                {version.displayName ?? version.name}
              </Text>
            </Pressable>
          );
        })}
      </React.Fragment>
    );
  };

  const renderModal = (): React.ReactElement => {
    return (
      <Modal
        visible={showModal}
        transparent={true}
        statusBarTranslucent={true}
        animationType='fade'>
        <View style={ControlsStyleSheet.modalBackground}>
          <View style={QuickAddListSheet.quickAddListContainer}>
            <ScrollView style={{padding: 20}}>
              <Text style={QuickAddListSheet.quickAddListHeading}>
                Launch Quick Report
              </Text>
              {renderTemplates()}
            </ScrollView>
          </View>
          <Pressable
            style={({pressed}) => [
              QuickAddListSheet.quickAddButton,
              pressed && {
                opacity: 0.8,
              },
            ]}
            onPress={() => setShowModal(false)}>
            <Image
              resizeMode='contain'
              style={{width: '100%', height: '100%'}}
              source={Images.CLOSE}
            />
          </Pressable>
        </View>
      </Modal>
    );
  };

  return (
    <React.Fragment>
      {showModal ? null : (
        <Pressable
          style={({pressed}) => [
            QuickAddListSheet.quickAddButton,
            pressed && {
              opacity: 0.8,
            },
          ]}
          onPress={() => setShowModal(true)}>
          <Image
            resizeMode='contain'
            style={{width: '100%', height: '100%'}}
            source={Images.FAB}
          />
        </Pressable>
      )}
      {renderModal()}
    </React.Fragment>
  );
};

export default QuickAddList;
